import React, { useState } from "react";
import { Stack, IconButton, TooltipHost } from "@fluentui/react";
import { useSearchPanel } from "../../../../theme/styles/SearchPanelStyle";
import { useTheme } from "../../../../theme/OAS365Theme";
import { FILE_TYPES } from "../../../../redux/constants/metaData";
import wordIcon from "../../../../assets/word.svg";
import excel from "../../../../assets/excel.svg";
import pdf from "../../../../assets/icons/pdf.png";
import powerpoint from "../../../../assets/powerpoint.svg";
import infoIcon from "../../../../assets/icons/information.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import searchIcon from "../../../../assets/icons/search.png";

const AllDocumentsSearchBar = ({
  searchValue,
  onSearchValueChange,
  onClearAndSearch,
  selectedFileType,
  onFileTypeChange,
  isFilterVisible,
  onFilterVisibleChange,
  isUserInvolvedOnly,
  onUserInvolvedOnlyChange,
  onSearch,
  searchLoading = false,
  placeholder = "Search for documents",
}) => {
  const theme = useTheme();
  const searchPanelStyle = useSearchPanel();
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isClearDisabled =
    searchValue === undefined || searchValue === null || searchValue === "";

  const handleClear = () => {
    if (typeof onClearAndSearch === "function") {
      onClearAndSearch();
      return;
    }
    onSearchValueChange("");
    onSearch();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const handleFilterClick = () => {
    if (searchLoading) return;
    onFilterVisibleChange(!isFilterVisible);
    if (isFilterVisible) {
      onFileTypeChange("");
    }
  };

  const filterButton = (fileType, label, icon) => {
    const isSelected = selectedFileType === fileType;
    const disabled = searchLoading;
    const containerStyle = {
      ...(isSelected
        ? searchPanelStyle.FileTypeButtonContainerSelectedStyle
        : searchPanelStyle.FileTypeButtonContainerStyle),
      ...(disabled && { pointerEvents: "none", opacity: 0.6 }),
    };
    return (
      <div
        key={fileType ?? "all"}
        style={containerStyle}
        onClick={() => !disabled && onFileTypeChange(fileType)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) =>
          !disabled && e.key === "Enter" && onFileTypeChange(fileType)
        }
        aria-disabled={disabled}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onFileTypeChange(fileType)}
          style={searchPanelStyle.FileTypeButtonStyle}
        >
          {icon}
          <span style={searchPanelStyle.FileTypeTextStyle}>{label}</span>
        </button>
      </div>
    );
  };

  return (
    <Stack
      horizontal
      wrap
      verticalAlign="center"
      tokens={{ childrenGap: 8 }}
      styles={{
        root: {
          padding: "8px 0",
          background: theme.colors.backgroundColor,
          flexWrap: "wrap",
          rowGap: "8px",
          alignItems: "center",
        },
      }}
    >
      <Stack
        horizontal
        styles={{
          root: {
            flex: "1 1 200px",
            minWidth: 200,
            maxWidth: 300,
            display: "flex",
            ...(isSearchFocused
              ? searchPanelStyle.ToolBarStylesHighlight?.root
              : searchPanelStyle.ToolBarStyles?.root),
          },
        }}
      >
        <input
          type="text"
          style={{
            ...searchPanelStyle.ToolBarSearchTextBoxStyle,
            flex: 1,
            minWidth: 0,
          }}
          placeholder={placeholder}
          value={searchValue ?? ""}
          disabled={searchLoading}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          onChange={(ev) => onSearchValueChange(ev.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search documents"
        />
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <IconButton
            styles={{
              root: isClearDisabled
                ? searchPanelStyle.StackSearchClearButtonToolBarDisableStyles
                : searchPanelStyle.StackSearchClearButtonToolBarStyles,
            }}
            disabled={isClearDisabled}
            iconProps={{ iconName: "Cancel" }}
            onClick={handleClear}
            ariaLabel="Clear search"
          />
          <div
            style={{ width: "2px", background: theme.colors.Addovation20Grey }}
          />
          <IconButton
            styles={{
              root: {
                ...(searchLoading
                  ? searchPanelStyle.SearchButtonToolBarDisabledStyle
                  : searchPanelStyle.SearchButtonToolBarStyle),
              },
              icon: {
                fontSize: "12px",
              },
            }}
            disabled={searchLoading}
            iconProps={{
              imageProps: { src: searchIcon, width: 18, height: 18, alt: "Search Icon" },
              imageSize: 24,
            }}
            onClick={onSearch}
            ariaLabel="Search"
          />
        </Stack>
      </Stack>

      <div
        style={{
          ...searchPanelStyle.FilterButtonContainerStyle,
          ...(searchLoading && { pointerEvents: "none", opacity: 0.6 }),
        }}
        onClick={handleFilterClick}
        role="button"
        tabIndex={searchLoading ? -1 : 0}
        onKeyDown={(e) =>
          !searchLoading && e.key === "Enter" && handleFilterClick()
        }
        aria-disabled={searchLoading}
      >
        <button
          type="button"
          disabled={searchLoading}
          onClick={handleFilterClick}
          style={searchPanelStyle.FilterButtonStyle}
        >
          {isFilterVisible ? "Filters <<" : "Filters >>"}
        </button>
      </div>

      {isFilterVisible && (
        <>
          {filterButton("", "All Files", null)}
          {filterButton(
            FILE_TYPES.docx,
            "Word",
            <img
              src={wordIcon}
              alt="Word"
              style={searchPanelStyle.IconImageStyle}
            />,
          )}
          {filterButton(
            FILE_TYPES.xlsx,
            "Excel",
            <img
              src={excel}
              alt="Excel"
              style={searchPanelStyle.IconImageStyle}
            />,
          )}
          {filterButton(
            FILE_TYPES.pptx,
            "PowerPoint",
            <img
              src={powerpoint}
              alt="PowerPoint"
              style={searchPanelStyle.IconImageStyle}
            />,
          )}
          {filterButton(
            FILE_TYPES.pdf,
            "PDF",
            <img src={pdf} alt="PDF" style={searchPanelStyle.PdfFilterIconImageStyle} />,
          )}

          <TooltipHost
            content={
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  alt="info"
                  src={infoIcon}
                  style={{ width: 14, height: 14, marginRight: 4 }}
                />
                <span>
                  {isUserInvolvedOnly
                    ? "Showing only documents I'm involved"
                    : "Showing all documents"}
                </span>
              </div>
            }
          >
            <div
              style={{
                ...(isUserInvolvedOnly
                  ? searchPanelStyle.AccessButtonContainerSelectedStyle
                  : searchPanelStyle.AccessButtonContainerStyle),
                ...(searchLoading && { pointerEvents: "none", opacity: 0.6 }),
              }}
              onClick={() =>
                !searchLoading && onUserInvolvedOnlyChange(!isUserInvolvedOnly)
              }
              role="button"
              tabIndex={searchLoading ? -1 : 0}
              onKeyDown={(e) =>
                !searchLoading &&
                e.key === "Enter" &&
                onUserInvolvedOnlyChange(!isUserInvolvedOnly)
              }
              aria-disabled={searchLoading}
            >
              <button
                type="button"
                disabled={searchLoading}
                onClick={() =>
                  !searchLoading && onUserInvolvedOnlyChange(!isUserInvolvedOnly)
                }
                style={searchPanelStyle.AccessButtonStyle}
              >
                {isUserInvolvedOnly ? (
                  <VisibilityOffIcon
                    style={{
                      color: theme.colors.addovationWhite,
                      fontSize: 20,
                    }}
                  />
                ) : (
                  <VisibilityIcon
                    style={{
                      color: theme.colors.addovation70Green,
                      fontSize: 20,
                    }}
                  />
                )}
              </button>
            </div>
          </TooltipHost>
        </>
      )}
    </Stack>
  );
};

export default AllDocumentsSearchBar;
